import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup";
import Select from "react-select";

// Markdown Editor
import {
  MDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import client from '../../../lib/feathers';
import { tierHasFeature } from '../../../lib/featureLevels';
import Modal from '../../Modal';
import Page from '../../Page';
import PrimaryButton from '../../PrimaryButton';
import FlightLibationList from './FlightLibationList';
import FlightCoverSelector from './FlightCoverSelector';


import putToStorage from '../../../lib/putToStorage';
const schema = yup.object({
  name: yup.string().required("Please enter a name for this flight"),
}).required();

export default function FormForm() {
  const { lid, fid } = useParams();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);

  // const [user, setLoggedInUser] = useState();
  const [currentFlight, setCurrentFlight] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [defaultCoverIndex, setDefaultCoverIndex] = useState(null);
  const [coverImageUploading, setCoverImageUploading] = useState(false);

  const [isLoading, setIsLoading] = useState();

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const loadFlight = async () => {
    setIsLoading(true);
    const service = client.service(`${user.role}/flight`);
    const response = await service.get(fid);

    setCurrentFlight(response);
    setValue('name', response.name);
    setValue('description', response.description);
    setValue('status', response.status);
    setCoverImage(response.coverImage || null);
    setDefaultCoverIndex(response.defaultCoverIndex);

    setIsLoading(false);
  }

  useEffect(() => {
    if (user) {
      loadFlight();
    }
  }, [fid, user]);

  const handleUpdateFlight = async (data) => {
    try {
      // service to update the flight itself
      const service = client.service(`${user.role}/flight`)
      await service.patch(currentFlight.id, {
        ...data,
        defaultCoverIndex: coverImage ? null : defaultCoverIndex,
      });

      navigate(`/${user.role}/listings/${lid}/flights`);
    } catch (error) {
      console.error("Error updating flight: ", error);
    }
  }

  const handleSelectDefaultCover = async (index) => {
    try {
      const service = client.service(`${user.role}/flight`);
      await service.patch(currentFlight.id, {
        defaultCoverIndex: index,
        coverImageAttachmentId: null,
      });
      setDefaultCoverIndex(index);
      setCoverImage(null);
    } catch (error) {
      console.error("Error setting default cover: ", error);
    }
  }

  const handleUploadCustomCover = async (file) => {
    setCoverImageUploading(true);
    try {
      const uploadService = client.service(`${user.role}/flight-cover-upload`);

      // Get signed URL for upload
      const signedUrlData = await uploadService.create({
        flightId: fid,
        'file-type': file.type,
      });

      // Upload to S3
      await putToStorage(signedUrlData.signedRequest, file);

      // Create attachment record and update flight
      const result = await uploadService.patch(fid, {
        path: signedUrlData.path,
        mime: file.type,
      });

      setCoverImage(result.coverImage);
      setDefaultCoverIndex(null);
    } catch (error) {
      console.error("Error uploading cover image: ", error);
      alert(error.message || 'Failed to upload cover image');
    } finally {
      setCoverImageUploading(false);
    }
  }

  const handleRemoveCover = async () => {
    if (!window.confirm('Are you sure you want to remove the cover image?')) return;
    try {
      const uploadService = client.service(`${user.role}/flight-cover-upload`);
      await uploadService.remove(fid);
      setCoverImage(null);
      setDefaultCoverIndex(null);
    } catch (error) {
      console.error("Error removing cover image: ", error);
    }
  }

  const handleDeleteFlight = async () => {
    const service = client.service(`${user.role}/flight`);
    await service.remove(fid);
    navigate(`/${user.role}/listings/${lid}/flights`);
  };

  // Tailwind Styles //
  const errorFocus = 'border-red-400 focus:border-red-500 focus:ring-red-500';
  const nonErrorFocus = 'border-gray-300 focus:ring-gray-500 focus:border-gray-500';

  let statusOptions = [
    { label: "Active", value: "active" },
    { label: "Archived", value: "archived" },
    { label: "Draft", value: "draft" }
  ];

  const SaveToolBar = () => (
    <div className="flex justify-between pt-5">
      <PrimaryButton onClick={() => setShowDeleteModal(true)}>Delete</PrimaryButton>
      <div>
        <Link
          to={`/${user?.role}/listings/${lid}/flights`}
          type="button"
          className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Cancel
        </Link>
        <PrimaryButton type="submit">Save</PrimaryButton>
      </div>
    </div>
  );

  return (
    <Page>
      {/* BREADCRUMBS */}
      {!isLoading ? (
        <>
          {/* <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
            <nav className="flex" aria-label="Breadcrumb">
              <Breadcrumb
                elements={[
                  { name: 'Listings', href: `/${user?.role}/listings` },
                  {
                    name: 'Listing Detail',
                    href: `/${user?.role}/listings/${lid}/`,
                  },
                  { name: 'Edit Flight' },
                ]}
              />
            </nav>
            <div className="mt-3 sm:mt-0 sm:ml-4" />
          </div> */}

          <form onSubmit={handleSubmit(handleUpdateFlight)}>
            <SaveToolBar />

            {/* CARD 1 */}
            <div className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
              {/* GRID */}
              <div className="md:grid md:grid-cols-3 md:gap-6">
                {/* COL 1 */}
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 inline-block align-middle mr-2">
                    Flight Information
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">Update the information for {currentFlight ? ` ${currentFlight.name}` : ' this flight.'}</p>
                  <br />
                  {currentFlight && (
                    <>
                      <dl className="whitespace-nowrap pb-1 py-1 text-sm font-medium text-gray-900 flex relative items-center">
                        <span className="flex items-center text-sm font-medium relative">
                          {currentFlight.createdBy !== null && (
                            <span>
                              <span className="text-black font-medium">Added By: </span>
                              <span className="text-sm text-gray-500">
                                {currentFlight.createdBy?.firstName} {currentFlight.createdBy?.lastName}
                                <br />
                                {currentFlight.createdBy?.email}
                              </span>
                            </span>
                          )}
                        </span>
                      </dl>
                      <dd className="whitespace-nowrap py-1 text-sm text-gray-500">
                        {currentFlight.createdAt && (
                          <span><span className="text-black font-medium">Added On: </span>{new Date(currentFlight.createdAt).toDateString()}</span>
                        )}
                      </dd>
                      <dd className="whitespace-nowrap py-5 text-sm text-gray-500" >
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field: { onChange, value, ref } }) => (
                            <Select
                              ref={ref}
                              options={statusOptions}
                              value={statusOptions.find((s) => s.value === value)}
                              onChange={(val) => onChange(val.value)}
                              styles={{
                                control: (baseStyles) => ({
                                  ...baseStyles,
                                  width: '50%',
                                }),
                              }}
                            />
                          )}
                        />
                      </dd>
                    </>
                  )}
                </div>

                {/* COL 2 */}
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <div className="grid grid-cols-6 gap-6">
                    {/* Cover Image Section */}
                    <div className="col-span-6">
                      <FlightCoverSelector
                        coverImage={coverImage}
                        defaultCoverIndex={defaultCoverIndex}
                        onSelectDefault={handleSelectDefaultCover}
                        onUploadCustom={handleUploadCustomCover}
                        onRemoveCover={handleRemoveCover}
                        isUploading={coverImageUploading}
                        canUploadCustom={user?.role === 'admin' || tierHasFeature(currentFlight?.listing?.subscriptionTier, 'CUSTOM_FLIGHT_COVER')}
                      />
                    </div>

                    <div className="col-span-6">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        {...register("name")}
                        className={`mt-1  block w-full shadow-sm sm:text-sm ${errors.name ? errorFocus : nonErrorFocus} rounded-md`}
                      />
                      {errors.name && <span className="text-red-400">{errors.name.message}</span>}
                    </div>

                    <div className="col-span-6">
                      <label htmlFor="winery" className="block text-sm font-medium text-gray-700">
                        Winery
                      </label>
                      <input
                        type="text"
                        id="winery"
                        disabled={true}
                        placeholder={currentFlight?.listing?.name}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 disabled:bg-gray-100 focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm rounded-md"
                      >
                      </input>
                    </div>
                    {/* MDX toolbar z index changed to avoid overlap with search menu */}
                    <div className="col-span-6 z-0">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                          <MDXEditor
                            ref={ref}
                            markdown={value || ''}
                            onChange={onChange}
                            className="border rounded-lg mt-1 h-52 overflow-y-auto [&_li]:ml-4 [&_ul]:list-disc [&_ol]:list-decimal"
                            plugins={
                              [
                                listsPlugin(),
                                markdownShortcutPlugin(),
                                toolbarPlugin({
                                  toolbarContents: () => (
                                    <>
                                      {' '}
                                      <UndoRedo />
                                      <BoldItalicUnderlineToggles />
                                      <ListsToggle />
                                    </>
                                  )
                                })
                              ]}
                          />
                        )}
                      />
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </form>

          {/* CARD 2 - Flight Builder*/}
          <FlightLibationList currentFlight={currentFlight} />
        </>
      ) : (
        <p>Loading Flight...</p>
      )}

      {showDeleteModal
        && (
          <Modal>
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                Confirm Deletion
              </h3>
              <div className="mt-2">
                <p className="text-base text-gray-500">
                  Are you sure you want to delete this flight?
                </p>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              <button type="button" onClick={handleDeleteFlight} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-icon text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:bg-red-700 sm:col-start-2 sm:text-sm">Confirm</button>
              <button type="button" onClick={() => setShowDeleteModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm">Cancel</button>
            </div>
          </Modal>
        )}
    </Page>
  )
}
